import logger from '@/utils/logger';
/**
 * 连接状态枚举
 */
export enum ConnectionState {
  IDLE = 'idle', // 空闲状态，未连接
  CONNECTING = 'connecting', // 连接中
  CONNECTED = 'connected', // 已连接
  DISCONNECTING = 'disconnecting', // 断开连接中
  ERROR = 'error', // 错误状态
  DESTROYING = 'destroying', // 销毁中（新增）
  DESTROYED = 'destroyed', // 已销毁
}

/**
 * 状态变更事件
 */
export interface StateChangeEvent {
  from: ConnectionState;
  to: ConnectionState;
  timestamp: number;
  context?: Record<string, any>;
}

/**
 * 状态变更回调函数
 */
export type StateChangeCallback = (event: StateChangeEvent) => void;

/**
 * 状态转换定义
 */
const STATE_TRANSITIONS: Record<ConnectionState, ConnectionState[]> = {
  [ConnectionState.IDLE]: [ConnectionState.CONNECTING, ConnectionState.DESTROYING],
  [ConnectionState.CONNECTING]: [
    ConnectionState.CONNECTED,
    ConnectionState.ERROR,
    ConnectionState.IDLE,
    ConnectionState.DESTROYING,
  ],
  [ConnectionState.CONNECTED]: [
    ConnectionState.DISCONNECTING,
    ConnectionState.ERROR,
    ConnectionState.DESTROYING,
  ],
  [ConnectionState.DISCONNECTING]: [
    ConnectionState.IDLE,
    ConnectionState.ERROR,
    ConnectionState.DESTROYING,
    ConnectionState.DESTROYED,
  ],
  [ConnectionState.ERROR]: [
    ConnectionState.CONNECTING,
    ConnectionState.IDLE,
    ConnectionState.DESTROYING,
    ConnectionState.DESTROYED,
  ],
  [ConnectionState.DESTROYING]: [ConnectionState.DESTROYED],
  [ConnectionState.DESTROYED]: [], // 终态，不能转换到其他状态
};

/**
 * 状态管理器 - 使用状态机模式管理连接状态
 */
export class StateManager {
  private state: ConnectionState = ConnectionState.IDLE;

  private history: StateChangeEvent[] = [];

  private listeners: Set<StateChangeCallback> = new Set();

  private maxHistorySize = 50; // 限制历史记录大小

  /**
   * 获取当前状态
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * 获取状态历史
   */
  getHistory(): StateChangeEvent[] {
    return [...this.history];
  }

  /**
   * 检查是否可以转换到目标状态
   */
  canTransition(to: ConnectionState): boolean {
    if (this.state === ConnectionState.DESTROYED) {
      return false; // 已销毁状态不能转换
    }

    const allowedStates = STATE_TRANSITIONS[this.state] || [];
    return allowedStates.includes(to);
  }

  /**
   * 状态转换
   */
  transition(to: ConnectionState, context?: Record<string, any>): boolean {
    if (!this.canTransition(to)) {
      logger.warn(`[StateManager] 无效的状态转换: ${this.state} -> ${to}`);
      return false;
    }

    const from = this.state;
    this.state = to;

    // 创建状态变更事件
    const event: StateChangeEvent = {
      from,
      to,
      timestamp: Date.now(),
      context,
    };

    // 添加到历史记录
    this.history.push(event);

    // 限制历史记录大小
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
    }

    // 触发监听器
    this.notifyListeners(event);

    logger.info(`[StateManager] 状态转换: ${from} -> ${to}`, context);
    return true;
  }

  /**
   * 添加状态变更监听器
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this.listeners.add(callback);

    // 返回取消监听的函数
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * 触发所有监听器
   */
  private notifyListeners(event: StateChangeEvent): void {
    this.listeners.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        logger.error('[StateManager] 状态监听器执行错误:', error);
      }
    });
  }

  /**
   * 强制重置状态（仅用于错误恢复）
   */
  forceReset(to: ConnectionState = ConnectionState.IDLE): void {
    logger.warn(`[StateManager] 强制重置状态: ${this.state} -> ${to}`);

    const from = this.state;
    this.state = to;

    const event: StateChangeEvent = {
      from,
      to,
      timestamp: Date.now(),
      context: { forced: true },
    };

    this.history.push(event);
    this.notifyListeners(event);
  }

  /**
   * 获取状态描述
   */
  getStateDescription(): string {
    const descriptions = {
      [ConnectionState.IDLE]: '空闲状态',
      [ConnectionState.CONNECTING]: '连接中',
      [ConnectionState.CONNECTED]: '已连接',
      [ConnectionState.DISCONNECTING]: '断开连接中',
      [ConnectionState.ERROR]: '错误状态',
      [ConnectionState.DESTROYING]: '销毁中',
      [ConnectionState.DESTROYED]: '已销毁',
    };

    return descriptions[this.state] || '未知状态';
  }

  /**
   * 获取最后一次错误
   */
  getLastError(): StateChangeEvent | null {
    return (
      this.history
        .slice()
        .reverse()
        .find((event) => event.to === ConnectionState.ERROR) || null
    );
  }

  /**
   * 销毁状态管理器
   */
  destroy(): void {
    // 标准销毁流程：任意态 -> DESTROYING -> DESTROYED
    if (this.state !== ConnectionState.DESTROYED) {
      if (this.canTransition(ConnectionState.DESTROYING)) {
        this.transition(ConnectionState.DESTROYING, { reason: 'destroy' });
      } else {
        // 若当前态不允许，采用强制重置到 DESTROYING 以保证一致性
        this.forceReset(ConnectionState.DESTROYING);
      }
      // DESTROYING 之后推进到 DESTROYED
      if (this.canTransition(ConnectionState.DESTROYED)) {
        this.transition(ConnectionState.DESTROYED, { reason: 'destroy' });
      } else {
        this.forceReset(ConnectionState.DESTROYED);
      }
    }
    this.listeners.clear();
  }

  /**
   * 获取调试信息
   */
  getDebugInfo(): Record<string, any> {
    return {
      currentState: this.state,
      stateDescription: this.getStateDescription(),
      historySize: this.history.length,
      listenersCount: this.listeners.size,
      lastStateChange: this.history[this.history.length - 1] || null,
      lastError: this.getLastError(),
    };
  }
}
